import type { GetUploadLink } from '../plugins/AzureBlobUploaderPlugin';
import type {
  FileUploadRestrictions,
  OnAfterUploadFn,
  UploaderMeta,
} from '../types';
import type { UppyLocale } from './getUppyLocale';
import type { Meta } from '@uppy/core';

import GoldenRetriever from '@uppy/golden-retriever';
import { useEffect } from 'react';

import { AzureBlobUploaderPlugin } from '../plugins/AzureBlobUploaderPlugin';

import { useUppyInstance } from './useUppyInstance';

type UploaderInstanceOptions<TMeta extends UploaderMeta> = {
  autoProceed: boolean;
  getUploadLink: GetUploadLink;
  id: string;
  language: string;
  meta?: TMeta;
  onAfterUpload?: OnAfterUploadFn<TMeta>;
  restrictions?: FileUploadRestrictions;
  uppyLocaleOverrides?: Partial<UppyLocale['strings']>;
};

/**
 * The uploading variant of `useUppyInstance`: adds the Azure Blob uploader
 * and GoldenRetriever, which restores a queue interrupted by a page reload.
 * `getUploadLink` and `onAfterUpload` may change between renders; the
 * uploader always calls the latest ones.
 */
export function useUploaderInstance<TMeta extends UploaderMeta>({
  getUploadLink,
  meta,
  onAfterUpload,
  ...options
}: UploaderInstanceOptions<TMeta>) {
  const instance = useUppyInstance({
    ...options,
    configure: (uppy) =>
      uppy
        .use(AzureBlobUploaderPlugin, {
          getUploadLink,
          onAfterUpload: onAfterUpload as OnAfterUploadFn | undefined,
        })
        .use(GoldenRetriever),
    meta: meta as Meta,
  });
  const { uppy } = instance;

  useEffect(() => {
    uppy.getPlugin('AzureBlobUploader')?.setOptions({
      getUploadLink,
      onAfterUpload: onAfterUpload as OnAfterUploadFn | undefined,
    });
  }, [getUploadLink, onAfterUpload, uppy]);

  return instance;
}
